/**
 * Bench state (BENCH-UX-SPEC §1–3): the key, dialogs, the current run and the job list. Main-thread app state (SD-2): the
 * key is never sent to the analysis worker.
 */
import { computed, signal } from '@preact/signals';
import type { RunCostEstimate } from '../../engine/bench/cost.ts';
import type { Severity } from '../../engine/bench/failure-report.ts';
import { GUARDRAILS_DEFAULT, GUARDRAILS_OFFERED } from '../../engine/bench/guardrails-flags.ts';
import { DEFAULT_KEEP, type KeepTier } from '../../engine/bench/keep.ts';
import type { Lens2 } from '../../engine/bench/lens2.ts';
import type { RunReport } from '../../engine/bench/loop2.ts';
import type { BenchMode } from '../../engine/bench/routes.ts';
import type { ChannelId } from '../../engine/channels/types.ts';
import type { ChunkBrief, ChunkTally } from '../../shared/bench-protocol.ts';
import { type AllowedModel, DEFAULT_MODEL, type Effort } from '../../shared/models.ts';
import type { AnalysisSummary } from '../../shared/summary.ts';
import type { BudgetState } from '../rewrite/budget.ts';
import type { LimiterState } from '../rewrite/limiter.ts';
import type { DocChain } from './chain.ts';
import type { BenchMessage } from './messages.ts';
import type { RunLive } from './run-live.ts';
import type { BenchJob } from './store.ts';

export type RewriteScope =
	| { kind: 'flagged' }
	| { kind: 'document' }
	| { kind: 'translation' }
	| { kind: 'channel'; id: ChannelId }
	| { kind: 'hotspot'; channel: ChannelId; paraStart: number; paraEnd: number }
	| { kind: 'selection'; paraStart: number; paraEnd: number };

export interface RunRequest {
	scope: RewriteScope;
	mode: BenchMode;
	only?: readonly ChannelId[];
}

export type BenchStage =
	| 'idle'
	| 'local-running'
	| 'local-done'
	| 'estimating'
	| 'confirm-cost'
	| 'running'
	| 'budget-paused'
	| 'done'
	| 'error';

/** The key: held only here and, opt-in, in `localStorage` (`prefs.ts`). */
export const apiKey = signal<string | null>(null);
export const keyRemembered = signal(false);
export const keyDialogOpen = signal(false);

export const economy = signal(false);
/** The run's model: the latest Sonnet by default (ED-26); Haiku 5.5 can be chosen in the cost dialog (ED-85). */
export const model = signal<AllowedModel>(DEFAULT_MODEL);
/** The run's effort (BENCH_EFFORT: `high`, the pilot's choice, DESIGN ED-58; spec §9.2, §14). */
export const effort = signal<Effort>('high');
/** The choices of the cost dialog's "requests at once" (DESIGN ED-86): the limiter starts at 4 and never passes this. */
export const CONCURRENCY_CHOICES = [1, 2, 4, 8] as const;
export const DEFAULT_CONCURRENCY = 8;
/** Requests at once on the Standard path (ED-86); Economy batches its rounds and ignores it. */
export const concurrency = signal<number>(DEFAULT_CONCURRENCY);
/** The choices of the cost dialog's "versions per passage" (BENCH-V3-PLAN §6, ED-90). */
export const VERSIONS_CHOICES = [1, 2, 3] as const;
/** Versions per passage one rewrite request asks for; the dialog starts it at the model's default (`defaultVersions`). */
export const versions = signal<number>(1);
/** The keep standard of the run (BENCH-V3-PLAN §7.1, ED-92), chosen in the cost dialog; strict is today's. */
export const keep = signal<KeepTier>(DEFAULT_KEEP);
/**
 * The editorial guardrails of the run (DESIGN ED-191, UD-41), chosen in the cost dialog; on by default (ED-192), off is the
 * baseline's requests. On adds the editorial rules to every request and asks for at most two versions, so the estimate prices it.
 */
export const guardrails = signal<boolean>(GUARDRAILS_OFFERED && GUARDRAILS_DEFAULT);
/** The lens lists only the passages with this severity of failure report (ED-93); `all` lists every passage. */
export const severityFilter = signal<Severity | 'all'>('all');
/** The rewrite export ends with the list of the lenient keeps (BENCH-V3-PLAN §7.3). */
export const exportAppendix = signal(false);
/** The limiter during a parallel run: the limit in force and the calls in flight and queued (the status line). */
export const parallelState = signal<LimiterState | null>(null);

export const stage = signal<BenchStage>('idle');
export const pendingRequest = signal<RunRequest | null>(null);
export const costEstimate = signal<RunCostEstimate | null>(null);
export const costDialogOpen = signal(false);
export const budgetState = signal<BudgetState | null>(null);
export const budgetDialogOpen = signal(false);
/** The notice above the bench: a fault (`error`) or an outcome that is not one (`info`), already worded (UD-32). */
export const runMessage = signal<BenchMessage | null>(null);
export const runProgress = signal<{ done: number; total: number } | null>(null);
/** Economy: the round in hand and whether the job is waiting for a batch (up to 24 hours a round, spec §11). */
export const economyRound = signal<{ round: number; waiting: boolean } | null>(null);

export const localOptions = signal({
	phrase: true,
	lex: true,
	scaffold: true,
	dash: true,
	pipe: true,
	quotes: false,
});
export const localResultText = signal<string | null>(null);
export const localEffectLines = signal<readonly string[]>([]);
/** The local pass's changelog: substitution label → count, in the seed's order (UD-20). */
export const localLog = signal<ReadonlyArray<readonly [string, number]>>([]);

/** The running (or last) rewrite's status summary (UD-20): outcomes so far and the stretch just finished. */
export interface RunSummary {
	jobId: string;
	startedAt: number;
	tally: ChunkTally;
	last: ChunkBrief | null;
}
export const runSummary = signal<RunSummary | null>(null);
export const runSummaryOpen = signal(false);
/** The live view of a Standard run (UD-20 for bench v3): the round, the requests, the stretches and the recent events. */
export const runLive = signal<RunLive | null>(null);

/**
 * The lens of a finished job (BENCH-V2-SPEC §13.3), tied to the document it was made for: it is shown only for that
 * `docHash` and cleared when a new analysis replaces the document (spec §15 item 8).
 */
export const lens = signal<Lens2 | null>(null);
export const lensOpen = signal(false);
/** The finished run, for the reverts of §13.1. */
export const lastReport = signal<RunReport | null>(null);
/** The job whose run the lens shows (its reverts change the latest version only when that job made it, UD-27 P2). */
export const lensJobId = signal<string | null>(null);
/** Units the customer reverted to their originals (a kept failure, a chunk, or all of them). */
export const revertedUnits = signal<ReadonlySet<string>>(new Set());
/** The latest version's text (the rewritten-text exports), or null while the chain holds only the original (UD-27). */
export const rewrittenText = signal<string | null>(null);

/** The open document's version chain (DESIGN UD-27, `chain.ts`), or null before it is loaded. */
export const chain = signal<DocChain | null>(null);
/**
 * The document whose saved versions are waiting for the customer's answer (UD-28), or null. While it is set the bar shows the
 * question, the bench actions are refused, and nothing changes the chain by itself.
 */
export const versionsAsk = signal<string | null>(null);
/** The question's dialog is on screen (Escape closes it; the question stays until it is answered or the document changes). */
export const versionsAskOpen = signal(false);
/** The version on screen: 0 is the original. The next action always works on the latest version. */
export const viewing = signal(0);
/** A version's full analysis in progress (the bar and the status bar show it), or null. */
export const versionWork = signal<{ n: number; fraction: number } | null>(null);
/** Why the last version could not be analysed, or null. */
export const versionError = signal<string | null>(null);

/** The latest version's number (0: only the original). */
export const latestN = computed(() => chain.value?.versions.length ?? 0);

/** Whether the lens may revert passages: its job made the latest version (P2: reverts on the latest step only). */
export const lensEditable = computed(() => {
	const c = chain.value;
	const id = lensJobId.value;
	const last = c?.versions.at(-1);
	return id !== null && last?.made.kind === 'job' && last.made.jobId === id;
});

/** The paragraphs that hold a kept-best rewrite the customer has not reverted: the reader marks them (spec §13.1). */
export const keptBestParas = computed<ReadonlySet<number>>(() => {
	const l = lens.value;
	const gone = revertedUnits.value;
	const out = new Set<number>();
	if (l)
		for (const u of l.units)
			if (u.keptBest && !gone.has(u.id)) for (let p = u.para; p < u.paraEnd; p++) out.add(p);
	return out;
});

export const jobs = signal<readonly BenchJob[]>([]);
/** The job this page is running right now (Standard or Economy), or null: the list shows it as running, with Details. */
export const liveJobId = signal<string | null>(null);
/** The Standard jobs another tab of this browser is running (their run lock is held there; UD-38): no Resume or Remove here. */
export const jobsElsewhere = signal<ReadonlySet<string>>(new Set());
export const jobsOpen = signal(false);

/**
 * The before/after overview of an opened rewrite (UD-22): the job, the summaries of the original document and of the rewritten
 * text (null while one is being made), and the channels that rose above their baseline.
 */
export interface OverviewData {
	job: BenchJob;
	before: AnalysisSummary | null;
	after: AnalysisSummary | null;
	over: readonly ChannelId[];
}
export const overview = signal<OverviewData | null>(null);
export const overviewOpen = signal(false);
/**
 * The overview's measurements in progress: where they started (the `performance.now()` clock), their prior, the fraction of
 * all of them, and the side being measured (the original working text, or the rewritten text).
 */
export const overviewProgress = signal<{
	startedAt: number;
	priorMs: number | null;
	fraction: number;
	side: 'before' | 'after';
} | null>(null);
export const overviewError = signal<string | null>(null);
